import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { BrandLockup } from "./Logo";
import { BOOKING_URL, scrollToId } from "../data/content";

const LINKS = [
    ["About", "#about"],
    ["Why Visit", "#why-visit"],
    ["Collections", "#collections"],
    ["Experience", "#experience"],
    ["George Town", "#george-town"],
    ["Visit Us", "#visit"],
    ["FAQ", "#faq"],
];

const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 48);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (e, hash) => {
        e.preventDefault();
        setOpen(false);
        scrollToId(hash);
    };

    return (
        <header
            data-testid="header-nav"
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
                scrolled || open
                    ? "border-b border-white/10 bg-heritage-ink/85 backdrop-blur-md"
                    : "border-b border-transparent bg-transparent"
            }`}
        >
            <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
                <a
                    href="#top"
                    data-testid="nav-brand-logo"
                    onClick={(e) => go(e, "#top")}
                    aria-label="IHCC Penang — back to top"
                >
                    <BrandLockup dark />
                </a>

                <nav data-testid="nav-links-list" className="hidden items-center gap-5 xl:flex">
                    {LINKS.map(([label, hash]) => (
                        <a
                            key={hash}
                            href={hash}
                            onClick={(e) => go(e, hash)}
                            data-testid={`nav-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                            className="whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.18em] text-heritage-parchment/70 transition-colors duration-300 hover:text-heritage-gold"
                        >
                            {label}
                        </a>
                    ))}
                    <a
                        href={BOOKING_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="nav-book-tickets-button"
                        className="whitespace-nowrap rounded-full bg-heritage-terra px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-heritage-parchment transition-all duration-300 hover:scale-[1.03] hover:bg-heritage-vermilion"
                    >
                        Book Tickets
                    </a>
                </nav>

                <button
                    data-testid="nav-mobile-menu-button"
                    onClick={() => setOpen(!open)}
                    className="text-heritage-parchment xl:hidden"
                    aria-label={open ? "Close menu" : "Open menu"}
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden border-t border-white/10 bg-heritage-ink/95 backdrop-blur-md xl:hidden"
                    >
                        <div className="flex flex-col gap-1 px-6 py-6">
                            {LINKS.map(([label, hash]) => (
                                <a
                                    key={hash}
                                    href={hash}
                                    onClick={(e) => go(e, hash)}
                                    data-testid={`nav-mobile-link-${label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="py-2.5 font-mono text-xs uppercase tracking-[0.25em] text-heritage-parchment/80"
                                >
                                    {label}
                                </a>
                            ))}
                            <a
                                href={BOOKING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="nav-mobile-book-tickets-button"
                                className="mt-4 rounded-full bg-heritage-terra px-6 py-3 text-center font-mono text-xs uppercase tracking-[0.25em] text-heritage-parchment"
                            >
                                Book Tickets
                            </a>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Nav;
